import { useEffect, useRef } from 'react';
import { MrsLink } from '@/components/MrsLink';
import { MRS_HOVER_CARD_ID } from './mrsHoverCard';
import { useMrsHoverCardStore, type MrsChipInfo } from './mrsHoverCardStore';

export type MrsChipProps = MrsChipInfo;

/**
 * PT-7 B1 — an MRS chip that opens the page's ONE hover card on hover or keyboard focus.
 *
 * It is `MrsLink variant="chip"` (the chip IS the pill, owner 2026-09-22), still a link that opens the MRS in a
 * new tab and still stopping propagation so a click does not toggle the Items row. It mounts no Radix root and
 * subscribes to no store: its handlers call the store through `getState()`, so opening the card re-renders the
 * card alone.
 *
 * The chip's native `title` is dropped — a native tooltip and the card at once is two popups — and its words
 * move to `aria-label`. While focused it points `aria-describedby` at the card, written straight to the DOM so a
 * focus change re-renders nothing (React does not own the attribute).
 */
export function MrsChip(props: MrsChipProps) {
  const { materialRequestId, mrsNumber, picklistId } = props;
  const elementRef = useRef<HTMLElement | null>(null);

  // The chip leaving the DOM (the Items search filtered its row away under the card) closes the card when it is
  // the open chip. Filtering changes no store state, so the host cannot notice on its own.
  useEffect(
    () => () => {
      if (elementRef.current) useMrsHoverCardStore.getState().closeIfOpenChip(elementRef.current);
    },
    [],
  );

  const openFor = (element: HTMLElement) => {
    elementRef.current = element;
    const store = useMrsHoverCardStore.getState();
    store.open(element, props);
    store.ensureSummaries(picklistId);
  };

  return (
    <MrsLink
      mrsId={materialRequestId}
      mrsNumber={mrsNumber}
      variant="chip"
      linkProps={{
        title: undefined,
        'aria-label': `${mrsNumber} — opens in a new tab`,
        onPointerEnter: (e) => openFor(e.currentTarget),
        onPointerLeave: () => useMrsHoverCardStore.getState().scheduleClose(),
        onFocus: (e) => {
          e.currentTarget.setAttribute('aria-describedby', MRS_HOVER_CARD_ID);
          openFor(e.currentTarget);
        },
        onBlur: (e) => {
          e.currentTarget.removeAttribute('aria-describedby');
          useMrsHoverCardStore.getState().close();
        },
      }}
    />
  );
}
